<template>
    <div
        data-testid="carousel"
        :class="['w-full py-4 md:rounded-lg', disableShadow ? '' : 'shadow']"
    >
        <div class="flex max-md:container mb-4 md:px-4">
            <div class="flex-grow">
                <slot name="header"></slot>
            </div>
            <div class="flex-none hidden md:inline-block">
                <Btn
                    data-testid="nav-left"
                    :variant="BtnVariant.TEXT"
                    :class="index === 0 ? 'opacity-05' : 'opacity-30'"
                    icon="chevron-circled-left"
                    :disabled="index === 0"
                    @click.prevent.stop="moveLeft"
                />
                <Btn
                    data-testid="nav-right"
                    :variant="BtnVariant.TEXT"
                    :class="index === limit ? 'opacity-05' : 'opacity-30'"
                    icon="chevron-circled-right"
                    :disabled="index === limit"
                    @click.prevent.stop="moveRight"
                />
            </div>
        </div>

        <div class="max-md:container md:px-4">
            <slot name="content"></slot>
        </div>

        <div
            ref="container"
            :style="`height: ${containerHeight}px`"
            class="relative overflow-hidden"
        >
            <div
                data-testid="slides"
                class="slides absolute w-full max-md:container overflow-x-scroll scrolling-touch md:px-4 md:overflow-hidden"
            >
                <div
                    ref="slides"
                    class="flex gap-x-3"
                    :style="slidesStyle"
                >
                    <slot name="slides"></slot>
                </div>
            </div>
        </div>

        <div class="max-md:container md:px-4">
            <slot name="footer"></slot>
        </div>
    </div>
</template>

<script>
import { BtnVariant } from '@components/Btn';
import { debounce } from 'lodash-es';

/** @DEPRECATED - refactor this to use @components/CarouselNew instead */
export default {
    name: 'Carousel',
    props: {
        disableShadow: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['carousel-moved-left', 'carousel-moved-right'],
    data() {
        return {
            BtnVariant,
            offset: 0,
            index: 0,
            limit: 0,
            total: 0,
            containerHeight: 0,
            slidesWidth: 0,
        };
    },
    computed: {
        slidesStyle() {
            let translateX = this.index * this.offset * -1;
            if (this.limit > 0 && this.index === this.limit) {
                const containerWidth = this.$refs.container.clientWidth;
                translateX = (this.slidesWidth + this.slidesPaddingLeft - containerWidth) * -1;
            }

            return {
                '--transform-translate-x': `${translateX}px`,
                '--width': `${this.slidesWidth}px`,
            };
        },
        slidesPaddingLeft() {
            let { paddingLeft } = window.getComputedStyle(this.$refs.container.children[0]);
            paddingLeft = /\d+/.exec(paddingLeft) || [0];
            return parseInt(paddingLeft[0], 10);
        },
    },
    async mounted() {
        await this.$nextTick();
        this.init();
        window.addEventListener('resize', this.debouncedInit);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.debouncedInit);
    },
    methods: {
        // eslint-disable-next-line func-names
        debouncedInit: debounce(function () {
            this.init();
        }, 150),
        init() {
            this.index = 0;
            this.total = this.$refs.slides.childElementCount;
            const containerWidth = this.$refs.container.clientWidth;

            let width = 0;
            let displayed = 0;

            Object.values(this.$refs.slides.children).forEach((child) => {
                const style = window.getComputedStyle(child);
                const marginLeft = /\d+/.exec(style.marginLeft) || [0];
                const marginRight = /\d+/.exec(style.marginRight) || [0];

                width += child.clientWidth + parseInt(marginLeft[0], 10) + parseInt(marginRight[0], 10);

                // take into account gap-x-3
                if (this.index < this.total - 1) {
                    width += 12;
                }

                if (width <= containerWidth) {
                    displayed += 1;
                }
                if (child.clientHeight > this.containerHeight) {
                    this.containerHeight = child.clientHeight;
                }
            });

            this.offset = width / this.total;
            this.limit = this.total - displayed;
            this.slidesWidth = width + this.slidesPaddingLeft;
        },
        moveLeft() {
            this.index -= 1;
            this.$emit('carousel-moved-left');
        },
        moveRight() {
            this.index += 1;
            this.$emit('carousel-moved-right');
        },
    },
};
</script>

<style lang="scss" scoped>
.slides {
    max-width: 100%;

    /* Hide scrollbar for IE, Edge and Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    /* Hide scrollbar for Chrome, Safari and Opera */
    &::-webkit-scrollbar {
        display: none;
    }

    & > div {
        --transform-translate-x: 0;
        --width: 100%;
        transform: translateX(var(--transform-translate-x));
        width: var(--width);
        transition: all 600ms ease;

        & > * {
            @apply inline-block mr-3;

            &:last-child {
                @apply mr-0;
            }
        }
    }
}
</style>
